import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import AlunniSendBar from "@/components/AlunniSendBar";
import useAlunniSelection from "@/lib/useAlunniSelection";

/**
 * Pagina "ISEE" in Segremat Web Pro.
 *
 * Lista verticale degli alunni ordinati per settore + classe + cognome + nome.
 * Riprende la colonna ISEE della tabella Mensa Mese con lo stesso bottone (📋)
 * per aprire lo storico ISEE per ogni anno.
 *
 * Differenze rispetto a Mensa Mese:
 * - Mostra TUTTI gli alunni dell'AS (non solo Infanzia).
 * - Selettore "Anno ISEE" indipendente dall'AS.
 *
 * Richiesta utente 2026-02-17.
 */

const fmtImporto = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function IseeInputCell({ alunno, annoIsee, onSaved }) {
  const initial = alunno.isee != null ? String(alunno.isee).replace(".", ",") : "";
  const [val, setVal] = useState(initial);
  useEffect(() => { setVal(alunno.isee != null ? String(alunno.isee).replace(".", ",") : ""); }, [alunno.CodFis, alunno.isee]);
  const commit = async () => {
    if (!alunno.CodFis) return;
    const cleaned = val.trim().replace(/\./g, "").replace(",", ".");
    if (cleaned === "" && alunno.isee == null) return;
    if (cleaned !== "" && Number(cleaned) === Number(alunno.isee || 0)) return;
    const n = cleaned === "" ? 0 : Number(cleaned);
    if (Number.isNaN(n)) { setVal(initial); return; }
    try {
      await api.put(`/mensa-mese/isee/${alunno.CodFis}`, null, {
        params: { anno_scol: annoIsee, importo: n },
      });
      onSaved();
    } catch (e) {
      alert("Errore salvataggio ISEE: " + (e.response?.data?.detail || e.message));
      setVal(initial);
    }
  };
  return (
    <input type="text" inputMode="decimal"
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      disabled={!alunno.CodFis}
      placeholder={alunno.CodFis ? "—" : "no CF"}
      data-testid={`isee-input-${alunno.Cod}`}
      style={{
        width: "100%", padding: "4px 6px",
        border: "1px solid #b2dfdb",
        background: alunno.isee != null ? "#e0f7fa" : "white",
        color: "#006064", fontSize: 12, fontWeight: 700,
        fontFamily: "Consolas, monospace",
        textAlign: "right", boxSizing: "border-box",
      }}
    />
  );
}

function StoricoIseeModal({ alunno, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ anno: new Date().getFullYear(), importo: "", data_emissione: "", data_scadenza: "", data_ricezione: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/mensa-mese/isee/by-cf/${alunno.CodFis}`);
      setList(r.data?.data || []);
    } catch { setList([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (alunno?.CodFis) load(); /* eslint-disable-next-line */ }, [alunno?.CodFis]);

  const submit = async () => {
    if (!form.anno || !form.importo) { alert("Anno e Importo sono obbligatori"); return; }
    setBusy(true);
    try {
      await api.post("/mensa-mese/isee", {
        cod_fis: alunno.CodFis,
        anno: parseInt(form.anno, 10),
        importo: parseFloat(String(form.importo).replace(/\./g, "").replace(",", ".")),
        data_emissione: form.data_emissione || null,
        data_scadenza: form.data_scadenza || null,
        data_ricezione: form.data_ricezione || null,
      });
      setForm({ anno: new Date().getFullYear(), importo: "", data_emissione: "", data_scadenza: "", data_ricezione: "" });
      await load();
      onChanged && onChanged();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  const delRecord = async (id) => {
    if (!window.confirm(`Eliminare il record ISEE id=${id}?`)) return;
    try {
      await api.delete(`/mensa-mese/isee/by-id/${id}`);
      await load();
      onChanged && onChanged();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const formatData = (s) => {
    if (!s) return "";
    const t = String(s).slice(0, 10);
    if (t.includes("-")) { const [y, m, d] = t.split("-"); return `${d}/${m}/${y}`; }
    return t;
  };

  return (
    <div onClick={onClose} data-testid="isee-storico-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "min(920px, 95vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 30px rgba(0,0,0,0.4)", border: "2px solid #00838f" }}>
        <div style={{ background: "#00838f", color: "white", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: 0.5, textTransform: "uppercase" }}>Storico ISEE</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{alunno.Cog} {alunno.Nom}</div>
            <div style={{ fontSize: 11, opacity: 0.9, fontFamily: "Consolas, monospace" }}>{alunno.CodFis || "(no CF)"}</div>
          </div>
          <button onClick={onClose} data-testid="isee-storico-close"
            style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,0.5)", padding: "4px 14px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>Chiudi ✕</button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: "#00838f" }}>
            📜 Storico ISEE registrati ({loading ? "…" : list.length})
          </div>
          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</div>
          ) : list.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888", border: "1px dashed #ccc" }}>
              Nessun record ISEE per questo alunno.
            </div>
          ) : (
            <table className="sw-table" style={{ fontSize: 12 }}>
              <thead className="thead-blue"><tr>
                <th style={{ width: 50, textAlign: "center" }}>ID</th>
                <th style={{ width: 70 }}>Anno</th>
                <th style={{ width: 120, textAlign: "right" }}>Importo</th>
                <th>Emissione</th>
                <th>Scadenza</th>
                <th>Ricezione</th>
                <th style={{ width: 60 }}></th>
              </tr></thead>
              <tbody>
                {list.map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: "center", fontFamily: "Consolas" }}>{r.id}</td>
                    <td style={{ fontWeight: 700, color: "#00838f" }}>{r.anno}</td>
                    <td style={{ textAlign: "right", fontFamily: "Consolas", fontWeight: 700 }}>€ {fmtImporto(r.importo)}</td>
                    <td>{formatData(r.data_emissione)}</td>
                    <td>{formatData(r.data_scadenza)}</td>
                    <td>{formatData(r.data_ricezione)}</td>
                    <td>
                      <button onClick={() => delRecord(r.id)} data-testid={`isee-del-${r.id}`}
                        style={{ padding: "2px 8px", background: "#c62828", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 18, padding: 12, border: "2px dashed #00838f", background: "#e0f7fa" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#006064", marginBottom: 8 }}>➕ Nuovo record ISEE</div>
            <div style={{ display: "grid", gridTemplateColumns: "auto 100px auto 140px", gap: 10, alignItems: "center", marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700 }}>Anno:</label>
              <input type="number" value={form.anno}
                onChange={e => setForm(f => ({ ...f, anno: e.target.value }))}
                data-testid="isee-form-anno"
                style={{ padding: "4px 6px", border: "1px solid #00838f", fontFamily: "Consolas", fontWeight: 700, fontSize: 13 }} />
              <label style={{ fontSize: 12, fontWeight: 700 }}>Importo €:</label>
              <input type="text" inputMode="decimal" value={form.importo}
                placeholder="es. 12500,50"
                onChange={e => setForm(f => ({ ...f, importo: e.target.value }))}
                data-testid="isee-form-importo"
                style={{ padding: "4px 6px", border: "1px solid #00838f", fontFamily: "Consolas", fontWeight: 700, fontSize: 13, textAlign: "right" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr auto 1fr", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <label style={{ fontSize: 11 }}>Emissione:</label>
              <input type="date" value={form.data_emissione}
                onChange={e => setForm(f => ({ ...f, data_emissione: e.target.value }))}
                style={{ padding: "3px 6px", border: "1px solid #b2dfdb", fontSize: 12 }} />
              <label style={{ fontSize: 11 }}>Scadenza:</label>
              <input type="date" value={form.data_scadenza}
                onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))}
                style={{ padding: "3px 6px", border: "1px solid #b2dfdb", fontSize: 12 }} />
              <label style={{ fontSize: 11 }}>Ricezione:</label>
              <input type="date" value={form.data_ricezione}
                onChange={e => setForm(f => ({ ...f, data_ricezione: e.target.value }))}
                style={{ padding: "3px 6px", border: "1px solid #b2dfdb", fontSize: 12 }} />
            </div>
            <div style={{ textAlign: "right" }}>
              <button onClick={submit} disabled={busy || !form.anno || !form.importo} data-testid="isee-form-submit"
                style={{ padding: "8px 18px", background: busy ? "#90a4ae" : "#00838f", color: "white", border: "none", fontSize: 13, fontWeight: 800, cursor: busy ? "wait" : "pointer", letterSpacing: 0.4 }}>
                {busy ? "Salvataggio…" : "💾 Aggiungi ISEE"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 🆕 Feb 2026: modal per copia manuale ISEE ad alunno target + creazione legame "fratello manuale"
function CopyIseeToAlunnoModal({ source, annoIsee, allAlunni, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const candidates = useMemo(() => {
    const t = (q || "").trim().toLowerCase();
    return (allAlunni || [])
      .filter(a => a.CodFis && a.Cod !== source.Cod)
      .filter(a => {
        if (!t) return true;
        const s = `${a.Cog || ""} ${a.Nom || ""} ${a.CodFis || ""}`.toLowerCase();
        return s.includes(t);
      })
      .slice(0, 30);
  }, [q, allAlunni, source]);

  const conferma = async () => {
    if (!target) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/mensa-mese/isee/copy-to-alunno/${source.CodFis}`, {
        target_cod_fis: target.CodFis,
        anno_scol: Number(annoIsee),
        link_as_sibling: true,
      });
      const msg = r.data.isee_created ? "creato nuovo record ISEE" : "record ISEE aggiornato";
      alert(`✓ ISEE copiato con successo (${msg}).\nLegame "fratello manuale" registrato.`);
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="isee-copy-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", borderRadius: 8, padding: 20, minWidth: 520, maxWidth: 700, maxHeight: "85vh", overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: "#6a1b9a" }}>📤 Copia ISEE a un altro alunno</h3>
            <div style={{ fontSize: 12, color: "#546e7a", marginTop: 4 }}>
              Sorgente: <b>{source.Cog} {source.Nom}</b> · CF <code>{source.CodFis}</code> · ISEE € {Number(source.isee).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · A.S. ISEE {annoIsee}
            </div>
          </div>
          <button onClick={onClose} data-testid="isee-copy-close"
            style={{ background: "transparent", border: "none", fontSize: 22, cursor: "pointer", color: "#999" }}>×</button>
        </div>
        <input autoFocus type="text" value={q} onChange={e => { setQ(e.target.value); setTarget(null); }}
          data-testid="isee-copy-search"
          placeholder="🔎 Cerca alunno target per Cognome, Nome, o Codice Fiscale"
          style={{ width: "100%", padding: "8px 12px", border: "1px solid #b39ddb", borderRadius: 4, fontSize: 13, marginBottom: 10, boxSizing: "border-box" }} />
        <div style={{ border: "1px solid #eee", borderRadius: 4, maxHeight: 320, overflow: "auto", marginBottom: 12 }}>
          {candidates.length === 0 && <div style={{ padding: 12, color: "#999", fontSize: 12, textAlign: "center" }}>Nessun alunno trovato</div>}
          {candidates.map(a => {
            const isSel = target && target.Cod === a.Cod;
            return (
              <div key={a.Cod} onClick={() => setTarget(a)}
                data-testid={`isee-copy-cand-${a.Cod}`}
                style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0",
                         background: isSel ? "#e1bee7" : "white",
                         fontWeight: isSel ? 700 : 500,
                         display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 13 }}>{a.Cog} {a.Nom}</div>
                  <div style={{ fontSize: 10, color: "#78909c", fontFamily: "Consolas" }}>{a.CodFis} · classe {a.classe || "—"}</div>
                </div>
                <div style={{ fontSize: 11, fontFamily: "Consolas", fontWeight: 700, color: a.isee != null ? "#c62828" : "#78909c" }}>
                  ISEE attuale: {a.isee != null ? `€ ${Number(a.isee).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "assente"}
                </div>
              </div>
            );
          })}
        </div>
        {target && target.isee != null && (
          <div style={{ padding: 8, background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 4, fontSize: 11, color: "#e65100", marginBottom: 10 }}>
            ⚠ Il target ha già un ISEE per l&apos;A.S. {annoIsee} → verrà <b>SOVRASCRITTO</b>.
          </div>
        )}
        {err && <div style={{ padding: 8, background: "#ffebee", color: "#c62828", fontSize: 12, borderRadius: 4, marginBottom: 10 }}>⚠ {err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={busy} data-testid="isee-copy-cancel"
            style={{ padding: "8px 16px", background: "#eee", border: "1px solid #ccc", cursor: "pointer" }}>Annulla</button>
          <button onClick={conferma} disabled={!target || busy} data-testid="isee-copy-confirm"
            style={{ padding: "8px 16px", background: target && !busy ? "#6a1b9a" : "#ccc", color: "white", border: "none", cursor: target && !busy ? "pointer" : "not-allowed", fontWeight: 700 }}>
            {busy ? "⏳ Copio…" : "📤 Copia ISEE e Rendi Fratello"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function IseeAlunniPage() {
  const { annoCorrente } = useAuth();
  const [annoIsee, setAnnoIsee] = useState(null);
  const [data, setData] = useState({ items: [], anno_isee: null, total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [filterSet, setFilterSet] = useState("");
  const [filterCla, setFilterCla] = useState("");
  // 🆕 Feb 2026: filtro ISEE a 3 stati: "" (tutti) | "con" (solo con ISEE) | "senza" (solo senza ISEE)
  const [filtroIsee, setFiltroIsee] = useState("");
  const [modal, setModal] = useState(null); // alunno
  const [copyToModal, setCopyToModal] = useState(null); // {source: alunno}
  const fileInputRef = useRef(null);
  const [importBusy, setImportBusy] = useState(false);
  const sel = useAlunniSelection();

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset input subito
    if (!file) return;
    const replace = window.confirm(
      `Importa file "${file.name}"?\n\n` +
      "OK = SOSTITUISCI completamente la tabella ISEE (consigliato se ricaricato file completo).\n" +
      "Annulla per non procedere.\n\n" +
      "Se vuoi solo AGGIUNGERE / aggiornare i record senza cancellare gli altri, " +
      "rispondi OK e poi seleziona 'No' al prossimo prompt."
    );
    if (!replace) return;
    const replaceMode = window.confirm(
      "Modalità SOSTITUZIONE:\n\n" +
      "OK = svuota la tabella PRIMA dell'import (tutti i vecchi record vengono persi).\n" +
      "Annulla = mantieni i record esistenti e fai upsert (per CF+anno)."
    );
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/mensa-mese/isee/import?replace=${replaceMode ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data || {};
      alert(`✓ Import completato.\n\nImportati: ${d.importati || 0}\nScartati: ${d.scartati || 0}\nTotale righe: ${d.totale_righe || 0}\nFile: ${d.filename || ""}`);
      await load();
    } catch (ex) {
      alert("⚠ Errore import: " + (ex.response?.data?.detail || ex.message));
    } finally {
      setImportBusy(false);
    }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = {};
      if (annoIsee) params.anno_isee = annoIsee;
      const r = await api.get("/mensa-mese/isee/matrice", { params });
      setData(r.data);
      if (!annoIsee && r.data?.anno_isee) setAnnoIsee(Number(r.data.anno_isee));
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [annoIsee]);

  const setoriList = useMemo(() => Array.from(new Set(data.items.map(it => it.settore).filter(Boolean))).sort(), [data.items]);
  const classiList = useMemo(() => Array.from(new Set(data.items.filter(it => !filterSet || it.settore === filterSet).map(it => it.classe).filter(Boolean))).sort(), [data.items, filterSet]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.items.filter(it => {
      if (filterSet && it.settore !== filterSet) return false;
      if (filterCla && it.classe !== filterCla) return false;
      if (filtroIsee === "senza" && it.isee != null) return false;
      if (filtroIsee === "con" && it.isee == null) return false;
      if (q && !(`${it.Cog} ${it.Nom}`.toLowerCase().includes(q) || String(it.Cod).includes(q) || (it.CodFis || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [data.items, search, filterSet, filterCla, filtroIsee]);

  const conIsee = data.items.filter(it => it.isee != null).length;

  // Anni ISEE selezionabili: AS-3 .. AS+1
  const anniIseeOpts = useMemo(() => {
    if (!annoCorrente) return [];
    const out = [];
    for (let y = annoCorrente - 3; y <= annoCorrente + 1; y++) out.push(y);
    return out;
  }, [annoCorrente]);

  return (
    <div data-testid="isee-page" style={{ padding: "12px 20px" }}>
      <div className="sw-page-title">
        <h1>💰 ISEE Alunni</h1>
        <div className="sw-breadcrumb">Home &gt; ISEE</div>
      </div>

      <div className="sw-card">
        <div className="sw-card-body">
          <div className="sw-filter-row" style={{ flexWrap: "wrap", gap: 8 }}>
            <span className="sw-filter-label">🗓 Anno ISEE:</span>
            <select className="sw-filter-select" value={annoIsee || ""}
              onChange={e => setAnnoIsee(Number(e.target.value))}
              data-testid="isee-anno-select"
              style={{ minWidth: 90, fontWeight: 700, color: "#00838f" }}>
              {!annoIsee && <option value="">…</option>}
              {anniIseeOpts.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Cerca:</span>
            <input type="text" placeholder="cognome, nome, cod, CF…"
              value={search} onChange={e => setSearch(e.target.value)}
              data-testid="isee-search"
              className="sw-filter-input" style={{ width: 200 }} />
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Settore:</span>
            <select className="sw-filter-select" value={filterSet}
              onChange={e => { setFilterSet(e.target.value); setFilterCla(""); }}
              data-testid="isee-filter-settore" style={{ minWidth: 80 }}>
              <option value="">Tutti</option>
              {setoriList.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Classe:</span>
            <select className="sw-filter-select" value={filterCla}
              onChange={e => setFilterCla(e.target.value)} style={{ minWidth: 80 }}>
              <option value="">Tutte</option>
              {classiList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="sw-filter-label" style={{ marginLeft: 8 }}>Mostra:</span>
            <select data-testid="isee-filter-status" value={filtroIsee} onChange={e => setFiltroIsee(e.target.value)}
              title="Attivi = alunni con ISEE registrato"
              className="sw-filter-select"
              style={{ minWidth: 130, background: filtroIsee ? "#fff59d" : "white", fontWeight: filtroIsee ? 700 : 400 }}>
              <option value="">Tutti</option>
              <option value="con">Solo con dati inseriti</option>
              <option value="senza">Solo senza ISEE</option>
            </select>
            {(search || filterSet || filterCla || filtroIsee) && (
              <button className="sw-filter-btn" onClick={() => { setSearch(""); setFilterSet(""); setFilterCla(""); setFiltroIsee(""); }}>
                Pulisci
              </button>
            )}
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls"
              onChange={handleImport} style={{ display: "none" }} data-testid="isee-import-file" />
            <button onClick={() => fileInputRef.current?.click()} disabled={importBusy}
              data-testid="isee-import-btn"
              title="Importa file Excel con storico ISEE (colonne: Anno, Codice Fiscale, Importo Isee, Data Emissione, Data Scadenza). Verifica/aggiorna record per CF+Anno."
              style={{ padding: "6px 14px", background: importBusy ? "#bdbdbd" : "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: importBusy ? "wait" : "pointer", borderRadius: 3, marginLeft: 8 }}>
              {importBusy ? "Importazione…" : "📥 Importa Excel ISEE"}
            </button>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#444" }}>
              <b>{filtered.length}</b> / {data.total} alunni · ISEE compilati: <b style={{ color: "#00695c" }}>{conIsee}</b>
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#555", lineHeight: 1.4 }}>
            Clicca <b>📋</b> per aprire lo storico ISEE dell&apos;alunno e gestire i record (con date emissione/scadenza/ricezione). La modifica inline è stata rimossa: tutte le modifiche passano dallo storico per tracciare correttamente le date.
          </div>
        </div>
      </div>

      {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", margin: "8px 0", fontSize: 12 }}>{err}</div>}

      <div className="sw-card" style={{ marginTop: 8 }}>
        <div style={{ overflow: "auto", maxHeight: "75vh" }}>
          <AlunniSendBar selected={sel.selected} onClear={sel.clear}
            label={`ISEE ${annoIsee || ""}`} anno={annoCorrente} />
          <table className="sw-table" style={{ width: "100%" }}>
            <thead className="thead-blue">
              <tr>
                <th style={{ width: 28, textAlign: "center" }}>
                  <input type="checkbox" data-testid="isee-selall"
                    title="Seleziona/Deseleziona tutti i visibili"
                    checked={filtered.length > 0 && filtered.every(it => sel.isSelected(it.Cod))}
                    onChange={(e) => e.target.checked ? sel.selectAll(filtered.map(it => it.Cod)) : sel.deselectAll(filtered.map(it => it.Cod))} />
                </th>
                <th style={{ width: 40, textAlign: "center" }}>#</th>
                <th style={{ width: 50, textAlign: "center" }}>Sett.</th>
                <th style={{ width: 60, textAlign: "center" }}>Classe</th>
                <th style={{ width: 60, textAlign: "center" }}>Cod</th>
                <th style={{ minWidth: 220 }}>Cognome Nome</th>
                <th style={{ width: 140 }}>Codice Fiscale</th>
                <th style={{ width: 170, textAlign: "center" }}>ISEE {annoIsee || ""}</th>
                <th style={{ width: 130, textAlign: "center" }}
                    title="Percentuale contribuzione = 34 + (ISEE − 0) × (100 − 34) / (32.000 − 0), limitata tra 34% e 100%">
                  % Contribuzione
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="sw-loading">Caricamento…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="sw-empty"><p>Nessun alunno trovato</p></td></tr>
              ) : filtered.map((it, i) => (
                <tr key={it.Cod} data-testid={`isee-row-${it.Cod}`}
                    style={{ background: sel.isSelected(it.Cod) ? "#f3e5f5" : undefined }}>
                  <td style={{ textAlign: "center" }}>
                    <input type="checkbox" data-testid={`isee-sel-${it.Cod}`}
                      checked={sel.isSelected(it.Cod)} onChange={() => sel.toggle(it.Cod)} />
                  </td>
                  <td style={{ color: "#888", textAlign: "center", fontSize: 11 }}>{i + 1}</td>
                  <td style={{ textAlign: "center", fontWeight: 700 }}>{it.settore || "-"}</td>
                  <td style={{ textAlign: "center", fontWeight: 700 }}>{it.classe || "-"}</td>
                  <td style={{ textAlign: "center", fontFamily: "Consolas", fontSize: 11 }}>{it.Cod}</td>
                  <td style={{ fontWeight: 600 }}>{it.Cog} {it.Nom}</td>
                  <td style={{ fontFamily: "Consolas", fontSize: 10, color: it.CodFis ? "#37474f" : "#bbb" }}>
                    {it.CodFis || "—"}
                  </td>
                  <td style={{
                    background: it.isee != null ? "#aed581" : "#fafafa",
                    padding: "4px 6px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                      <div data-testid={`isee-val-${it.Cod}`} style={{
                        flex: 1, textAlign: "right", fontFamily: "Consolas, monospace",
                        fontWeight: 700, color: "#006064", fontSize: 12,
                      }}>
                        {it.isee != null ? `€ ${fmtImporto(it.isee)}` : "—"}
                      </div>
                      {it.CodFis && it.isee != null && (
                        <>
                        <button
                          data-testid={`isee-dup-siblings-${it.Cod}`}
                          onClick={async () => {
                            if (!window.confirm(`Duplicare l'ISEE di ${it.Cog} ${it.Nom} (€ ${fmtImporto(it.isee)}) ai suoi fratelli per l'anno ${annoIsee}?`)) return;
                            try {
                              const r = await api.post(`/mensa-mese/isee/duplicate-siblings/${it.CodFis}?anno_scol=${annoIsee}`);
                              const upd = (r.data.sibling_cfs_updated || []).length;
                              const nw = (r.data.sibling_cfs_no_isee_before || []).length;
                              const tot = r.data.total || 0;
                              if (tot === 0) alert("Nessun fratello trovato per questo alunno.");
                              else alert(`ISEE duplicato a ${tot} fratelli (${upd} aggiornati · ${nw} nuovi).`);
                              load();
                            } catch (e) {
                              alert("Errore duplica: " + (e.response?.data?.detail || e.message));
                            }
                          }}
                          title={`Duplica l'importo ISEE € ${fmtImporto(it.isee)} ai fratelli (stesso g1_cf o g2_cf o link manuale) per l'anno ${annoIsee}`}
                          style={{ padding: "1px 5px", background: "#f57c00", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                          🔁
                        </button>
                        <button
                          data-testid={`isee-copy-to-${it.Cod}`}
                          onClick={() => setCopyToModal({ source: it })}
                          title={`Copia MANUALMENTE l'ISEE € ${fmtImporto(it.isee)} ad un alunno a scelta e crea il legame "fratello manuale"`}
                          style={{ padding: "1px 5px", background: "#6a1b9a", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                          📤
                        </button>
                        </>
                      )}
                      {it.CodFis && (
                        <button onClick={() => setModal(it)} data-testid={`isee-open-${it.Cod}`}
                          title="Apri storico ISEE per modificare o aggiungere record (con date emissione/scadenza/ricezione)"
                          style={{ padding: "1px 5px", background: "#00838f", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                          📋
                        </button>
                      )}
                    </div>
                  </td>
                  {/* 🆕 Percentuale di contribuzione = 34 + ISEE × (100−34) / (32000−0), clamp [34%, 100%] */}
                  {(() => {
                    const ISEE_INI = 0, ISEE_FIN = 32000, PCT_MIN = 34, PCT_MAX = 100;
                    let cellBg = "#fafafa", label = "—", color = "#bbb", tip = "ISEE non presente → percentuale non calcolabile";
                    if (it.isee != null && it.isee !== "") {
                      const n = Number(it.isee);
                      if (Number.isFinite(n)) {
                        let pct = PCT_MIN + (n - ISEE_INI) * (PCT_MAX - PCT_MIN) / (ISEE_FIN - ISEE_INI);
                        if (pct < PCT_MIN) pct = PCT_MIN;
                        if (pct > PCT_MAX) pct = PCT_MAX;
                        const isMin = pct <= PCT_MIN;
                        const isMax = pct >= PCT_MAX;
                        label = `${pct.toFixed(2).replace(".", ",")} %`;
                        color = isMax ? "#c62828" : (isMin ? "#1b5e20" : "#0d47a1");
                        cellBg = isMax ? "#ffebee" : (isMin ? "#e8f5e9" : "#e3f2fd");
                        tip = `ISEE € ${n.toLocaleString("it-IT", { minimumFractionDigits: 2 })} → ${pct.toFixed(2)}%${isMin ? " (minimo)" : isMax ? " (massimo)" : ""}`;
                      }
                    }
                    return (
                      <td data-testid={`isee-pct-${it.Cod}`} title={tip}
                        style={{ textAlign: "center", background: cellBg, fontFamily: "Consolas, monospace", fontWeight: 800, color, fontSize: 12 }}>
                        {label}
                      </td>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <StoricoIseeModal alunno={modal} onClose={() => setModal(null)} onChanged={load} />
      )}
      {copyToModal && (
        <CopyIseeToAlunnoModal
          source={copyToModal.source}
          annoIsee={annoIsee}
          allAlunni={data.items}
          onClose={() => setCopyToModal(null)}
          onDone={() => { setCopyToModal(null); load(); }}
        />
      )}
    </div>
  );
}
